import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_ATTEMPTS = 7;
const POLL_INTERVAL = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const poll = async () => {
      if (stoppedRef.current) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("error");
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setData(res.data);
        if (res.data?.payment_status === "paid") {
          stoppedRef.current = true;
          setStatus("paid");
          return;
        }
        if (res.data?.status === "expired") {
          stoppedRef.current = true;
          setStatus("expired");
          return;
        }
      } catch (e) {
        // ignore, try again
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();

    return () => { stoppedRef.current = true; };
  }, [sessionId]);

  return (
    <div
      data-testid="success-page"
      className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
    >
      <div className="max-w-lg w-full bg-[#0f0f0f] border border-white/10 p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto text-[#d4af37] animate-spin" size={48} />
            <h1 className="mt-6 font-heading text-4xl uppercase">Processing Payment</h1>
            <p className="mt-3 text-white/60">
              Hold tight — we're confirming with Stripe.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#d4af37]" size={56} />
            <h1 className="mt-6 font-heading text-5xl uppercase text-[#d4af37]">
              Welcome In.
            </h1>
            <p className="mt-4 text-white/80">
              Payment received. Keep an eye on your inbox — we'll confirm your
              session or membership details within 24 hours.
            </p>
            {data && (
              <div className="mt-6 text-left bg-black/40 border border-white/10 p-5 text-sm">
                <Row label="Package">{data.package_id}</Row>
                <Row label="Amount">
                  ${data.amount} {String(data.currency || "").toUpperCase()}
                </Row>
                <Row label="Status">{data.payment_status}</Row>
              </div>
            )}
            <Link
              to="/"
              data-testid="success-home-link"
              className="mt-8 inline-flex px-8 py-3 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#f1c40f]"
            >
              Back to Home
            </Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="mt-6 font-heading text-4xl uppercase">Session Expired</h1>
            <p className="mt-3 text-white/70">Please try booking again.</p>
            <Link
              to="/"
              className="mt-8 inline-flex px-8 py-3 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#f1c40f]"
            >
              Back
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="mt-6 font-heading text-4xl uppercase">Status Unknown</h1>
            <p className="mt-3 text-white/70">
              We couldn't confirm your payment. Please check your email or contact us.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex px-8 py-3 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#f1c40f]"
            >
              Back
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-white/50 uppercase tracking-[0.15em] text-[11px]">
        {label}
      </span>
      <span className="text-white/90">{children}</span>
    </div>
  );
}
